// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState, useCallback } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Button, type ButtonProps } from '@mantine/core';
import { v4 as uuid } from 'uuid';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { type DetailSectionProps, DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';

type Props = {
  editable: boolean;
  children: (editing: boolean, finishEditing: () => void) => ReactNode;
  /** @default 'compact-md' */
  buttonSize?: ButtonProps['size'];
} & StrongOmit<DetailSectionProps, 'children'>;

export const EditableDetailSection = ({ editable, children, buttonSize = 'compact-md', ...props }: Props) => {
  const [editing, setEditing] = useState(false);
  const finishEditing = useCallback(() => setEditing(false), []);

  const [name] = useState(() => uuid());
  useBlockerWhen(`editable-detail-${name}`, editing);

  return (
    <DetailSection
      {...props}
      rightSection={
        <>
          {editable && !editing && (
            <Button variant="light" onClick={() => setEditing(true)} size={buttonSize}>
              <EvolveIcon icon="Edit" />
            </Button>
          )}
          {props.rightSection}
        </>
      }
    >
      {children(editing, finishEditing)}
    </DetailSection>
  );
};
